import React from "react";
import { useDraft } from "../../lib/draft-store";
import { BUDGET_BANDS } from "../../lib/budget-bands";
import { formatINR } from "../../lib/format";

const MODES = [
  { key: "band", label: "Yes — around a band" },
  { key: "max_amount", label: "I have a maximum budget" },
  { key: "flexible", label: "I am flexible" },
  { key: "exploring", label: "Just exploring" },
];

export default function Step5Budget() {
  const { payload, setField, mergePayload } = useDraft();
  const budget = payload?.budget || {};

  const setMode = (mode) => {
    mergePayload({
      budget: {
        mode,
        band: mode === "band" ? budget.band || null : null,
        max_amount: mode === "max_amount" ? budget.max_amount || "" : null,
      },
    });
  };

  return (
    <div data-testid="plan-step-5">
      <h1 className="font-display text-3xl text-[#1A1A1A] sm:text-4xl">
        We want to send you a quote that fits
      </h1>
      <p className="mt-3 max-w-xl text-base text-[#1A1A1A]/65">
        Do you have a budget in mind? It helps us tailor packages — never
        affects the artistry.
      </p>

      <div
        data-testid="budget-mode-group"
        role="radiogroup"
        aria-label="Budget mode"
        className="mt-8 space-y-3"
      >
        {MODES.map((m) => {
          const active = budget.mode === m.key;
          return (
            <button
              key={m.key}
              type="button"
              role="radio"
              aria-checked={active}
              data-testid={`budget-mode-${m.key}`}
              onClick={() => setMode(m.key)}
              className={[
                "flex w-full items-center justify-between rounded-xl border bg-white px-5 py-4 text-left transition",
                active
                  ? "border-[#C8A24B] ring-2 ring-[#C8A24B]/30"
                  : "border-[#EAE3D2] hover:border-[#C8A24B]",
              ].join(" ")}
            >
              <span className="text-sm text-[#1A1A1A]">{m.label}</span>
              <span
                className={[
                  "h-3.5 w-3.5 rounded-full border",
                  active
                    ? "border-[#C8A24B] bg-[#C8A24B]"
                    : "border-[#1A1A1A]/30",
                ].join(" ")}
              />
            </button>
          );
        })}
      </div>

      {budget.mode === "band" ? (
        <div
          data-testid="budget-bands-group"
          className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2"
        >
          {BUDGET_BANDS.map((b) => {
            const active = budget.band === b.id;
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`budget-band-${b.id}`}
                aria-pressed={active}
                onClick={() => setField(["budget", "band"], b.id)}
                className={[
                  "rounded-xl border bg-white p-4 text-left transition",
                  active
                    ? "border-[#C8A24B] ring-2 ring-[#C8A24B]/30"
                    : "border-[#EAE3D2] hover:border-[#C8A24B]",
                ].join(" ")}
              >
                <p className="font-display text-lg text-[#1A1A1A]">{b.label}</p>
                <p className="mt-1 text-xs text-[#1A1A1A]/55">{b.sub}</p>
                <p className="mt-2 text-sm text-[#C8A24B]">
                  {formatINR(b.min)}
                  {b.max ? ` – ${formatINR(b.max)}` : "+"}
                </p>
              </button>
            );
          })}
        </div>
      ) : null}

      {budget.mode === "max_amount" ? (
        <div className="mt-6 max-w-xs">
          <label htmlFor="max-amount" className="text-xs uppercase tracking-[0.22em] text-[#1A1A1A]/55">
            Maximum amount (₹)
          </label>
          <input
            id="max-amount"
            type="number"
            min="0"
            step="1000"
            data-testid="budget-max-amount"
            value={budget.max_amount || ""}
            onChange={(e) =>
              setField(["budget", "max_amount"], e.target.value === "" ? "" : Number(e.target.value))
            }
            className="mt-2 w-full rounded-lg border border-[#EAE3D2] bg-white px-4 py-3 text-sm text-[#1A1A1A] outline-none focus:border-[#C8A24B]"
            placeholder="200000"
          />
        </div>
      ) : null}
    </div>
  );
}

export function step5IsValid(payload) {
  const b = payload?.budget || {};
  if (!b.mode) return false;
  if (b.mode === "band") return Boolean(b.band);
  if (b.mode === "max_amount") return Number(b.max_amount) > 0;
  return true;
}
